type MuiSeverityColor = 'error' | 'warning' | 'info' | 'success' | 'default';

const DE_CANONICAL = ['kritisch', 'hoch', 'mittel', 'niedrig', 'info'] as const;
type DeSeverity = (typeof DE_CANONICAL)[number];

const EN_CANONICAL = ['critical', 'high', 'medium', 'low', 'informational', 'none'] as const;
type EnSeverity = (typeof EN_CANONICAL)[number];

const DE_ALIASES: Record<string, DeSeverity> = {
  kritisch: 'kritisch',
  hoch: 'hoch',
  mittel: 'mittel',
  niedrig: 'niedrig',
  info: 'info',
  informational: 'info',
};

const EN_ALIASES: Record<string, EnSeverity> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  informational: 'informational',
  info: 'informational',
  none: 'none',
};

function sanitizeSeverity(value?: string): string {
  return (value || '').trim().toLowerCase();
}

export function formatSeverityLower(value?: string): string {
  const sanitized = sanitizeSeverity(value);
  if (!sanitized || sanitized === '-') return '';
  if (sanitized === '–') return '–';
  if (DE_ALIASES[sanitized]) return DE_ALIASES[sanitized];
  if (EN_ALIASES[sanitized]) return EN_ALIASES[sanitized];
  return sanitized;
}

export function getKritikalitaetLower(value?: string): DeSeverity | undefined {
  const sanitized = sanitizeSeverity(value);
  return DE_ALIASES[sanitized] || undefined;
}

/** Aligns with DOCX export severity colors (docx_report_service.SEVERITY_COLORS). */
export const KRITIKALITAET_COLORS = {
  kritisch: '#8B0000',
  hoch: '#FF0000',
  mittel: '#FFA500',
  niedrig: '#FFC107',
  info: '#757575',
} as const satisfies Record<DeSeverity, string>;

export function getKritikalitaetMuiColor(value?: string): MuiSeverityColor {
  switch (getKritikalitaetLower(value)) {
    case 'kritisch':
      return 'error';
    case 'hoch':
      return 'warning';
    case 'mittel':
      return 'info';
    case 'niedrig':
      return 'success';
    case 'info':
    default:
      return 'default';
  }
}

export function getKritikalitaetHexColor(
  value?: string,
  defaultColor: string = KRITIKALITAET_COLORS.info,
): string {
  const key = getKritikalitaetLower(value);
  if (key) {
    return KRITIKALITAET_COLORS[key];
  }
  return defaultColor;
}

export function getKritikalitaetChipSx(value?: string) {
  const color = getKritikalitaetHexColor(value);
  return {
    fontWeight: 600,
    bgcolor: `${color}20`,
    color,
    border: `1px solid ${color}40`,
  };
}

export function getCvssSeverityLabelLower(score?: number): string {
  if (score === undefined || score === null) return '';
  if (score === 0) return 'none';
  if (score <= 3.9) return 'low';
  if (score <= 6.9) return 'medium';
  if (score <= 8.9) return 'high';
  return 'critical';
}
